import type { FC, ReactNode } from 'react'
import type { SongCard } from '@/types/content'
import type { Track } from '@/lib/tracks'

import Link from 'next/link'
import { BookOpen } from 'lucide-react'
import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'
import { formatDuration } from '@/lib/format'
import { songFormatLabels } from '@/lib/labels'
import { toTrack } from '@/lib/tracks'
import { CoverArt } from '@/components/media/cover-art'
import { DemoBadge } from '@/components/site/demo-badge'
import { PlayButton, PlayingBars } from '@/components/player/play-button'
import { usePlayer } from '@/components/player/player-provider'

interface SongRowProps {
    song: SongCard
    number?: number
    context?: Array<Track>
    showAlbum?: boolean
    showCover?: boolean
    tone?: 'dark' | 'paper'
}

export const SongRow: FC<SongRowProps> = ({ song, number, context, showAlbum = true, showCover = true, tone = 'dark' }): ReactNode => {
    const { current, isPlaying } = usePlayer()
    const active = current?.id === song.id

    return (
        <li className={cn('group grid grid-cols-[auto_1fr_auto] items-center gap-4 border-b py-3 sm:grid-cols-[2.25rem_auto_1fr_auto_auto]', tone === 'paper' ? 'border-ink/10' : 'border-white/[0.06]')}>
            <span className='hidden w-9 text-center font-brand text-xs tabular-nums text-muted-foreground sm:block'>
                {active && isPlaying ? <PlayingBars /> : number !== undefined ? String(number).padStart(2, '0') : ''}
            </span>

            {showCover ? (
                <Link href={`/music/${song.slug}`} className='relative size-12 shrink-0 overflow-hidden rounded' tabIndex={-1} aria-hidden='true'>
                    <CoverArt src={song.cover} alt='' seed={song.slug} className='absolute inset-0' sizes='48px' label={false} />
                </Link>
            ) : <span className='sm:hidden' />}

            <div className='min-w-0'>
                <p className='flex items-center gap-2'>
                    <Link href={`/music/${song.slug}`} className={cn('truncate font-heading text-lg leading-tight transition-colors hover:text-gold', active ? 'text-gold' : 'text-foreground')}>
                        <span {...cms('songs', song.id, 'title')}>{song.title}</span>
                    </Link>

                    {song.is_demo && <DemoBadge tone={tone === 'paper' ? 'paper' : 'dark'} />}
                </p>

                <p className='mt-0.5 flex items-center gap-2 truncate text-xs text-muted-foreground'>
                    <span>{song.is_instrumental ? 'Instrumental' : songFormatLabels[song.format]}</span>

                    {showAlbum && song.album && (
                        <>
                            <span aria-hidden='true'>&middot;</span>
                            <Link href={`/albums/${song.album.slug}`} className='truncate hover:text-foreground'>
                                <span {...cms('albums', song.album.id, 'title')}>{song.album.title}</span>
                            </Link>
                        </>
                    )}

                    {song.language && !song.is_instrumental && (
                        <>
                            <span aria-hidden='true'>&middot;</span>
                            <span {...cms('songs', song.id, 'language')}>{song.language}</span>
                        </>
                    )}
                </p>
            </div>

            <div className='hidden items-center gap-4 sm:flex'>
                {song.lyric_languages.length > 0 && (
                    <Link href={`/lyrics/${song.slug}`} className='text-muted-foreground transition hover:text-gold' aria-label={`Read the lyrics of ${song.title}`}>
                        <BookOpen className='size-4' aria-hidden='true' />
                    </Link>
                )}

                <span className='w-10 text-right text-xs tabular-nums text-muted-foreground'>
                    {song.duration_seconds ? formatDuration(song.duration_seconds) : ''}
                </span>
            </div>

            {song.has_audio
                ? <PlayButton track={toTrack(song)} context={context} size='sm' tone={tone === 'paper' ? 'solid' : 'glass'} />
                : <span className='w-9 text-center text-[0.6rem] uppercase tracking-[0.14em] text-muted-foreground'>Soon</span>}
        </li>
    )
}
